// Global Instructions Rule Applied!
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { List, Typography, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagnifyingGlass,
  faBuilding,
  faBriefcase,
  faAddressBook,
  faChartPie,
  faHandshake,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons'
import { selectRecents } from '../state/mobileRecentsSlice.ts'
import { RecordRow } from '../components/RecordRow.tsx'
import { Section } from '../components/ui.tsx'
import { BROWSE_MODULES, getDescriptor } from '../lib/listDescriptors.ts'
import { detailPath, browsePath, MOBILE_ROUTES, avatarInitials } from '../lib/routes.ts'

const RECENTS_SHOWN = 10

const MODULE_ICON: Record<string, any> = {
  companies: faBuilding,
  deals: faBriefcase,
  contacts: faAddressBook,
  funds: faChartPie,
  prospects: faHandshake
}

/**
 * Mobile landing (the PWA start screen + post-login redirect). Recently-viewed records
 * (from the persisted mobileRecents slice), module shortcuts into Browse, and a Search
 * entry — all driven by existing data, no per-screen fetch.
 */
export function HomeScreen(): React.JSX.Element {
  const navigate = useNavigate()
  const user = useSelector((s: any) => s.auth?.user)
  const recents = useSelector(selectRecents)

  const meta = (user?.user_metadata as Record<string, any>) || {}
  const displayName = meta.full_name || meta.name || user?.email || ''
  const firstName = String(displayName).trim().split(/\s+/)[0] || ''

  const shown = recents.slice(0, RECENTS_SHOWN)

  return (
    <div style={{ padding: 12 }}>
      {firstName ? <div style={{ fontWeight: 700, fontSize: 20, padding: '4px 4px 14px' }}>Hi {firstName}</div> : null}

      <Section title="Recently viewed">
        {shown.length === 0 ? (
          <Typography.Text type="secondary">Records you open will show up here.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={shown}
            rowKey={(it) => `${it.module}-${it.id}`}
            renderItem={(it) => (
              <RecordRow
                item={{
                  id: it.id,
                  module: it.module,
                  title: it.title,
                  subtitle: it.subtitle,
                  avatarText: avatarInitials(it.title)
                }}
                onClick={() => navigate(detailPath(it.module, it.id))}
              />
            )}
          />
        )}
      </Section>

      <Section title="Browse">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {BROWSE_MODULES.map((m) => {
            const descriptor = getDescriptor(m)
            if (!descriptor) return null
            return (
              <button
                key={m}
                type="button"
                onClick={() => navigate(browsePath(m))}
                style={{
                  flex: '1 1 45%',
                  minWidth: 140,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 10,
                  padding: '14px',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={MODULE_ICON[m] ?? faChevronRight} style={{ color: '#1890ff', fontSize: 18, width: 22 }} />
                <span style={{ fontSize: 15, fontWeight: 500 }}>{descriptor.label}</span>
              </button>
            )
          })}
        </div>
      </Section>

      <Button
        block
        size="large"
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        onClick={() => navigate(MOBILE_ROUTES.search)}
        style={{ height: 48 }}
      >
        Search
      </Button>
    </div>
  )
}

export default HomeScreen
